import { useState, useEffect } from 'react';
import { MapPin, Phone, PhoneCall, Mail, Facebook } from 'lucide-react';

import { apiService } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';

// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

import { CDN_URL, PRICE_LIST_PDF_URL } from '@/config/cdn';


// TikTok icon (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  // Use static data as initial state to avoid flash of default content
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [navLinks, setNavLinks] = useState(() => getStaticData('navigationLinks') || []);
  useEffect(() => {
    const fetchData = async () => {
      const [info, links] = await Promise.all([
        apiService.getBusinessInfo(),
        apiService.getNavigationLinks()
      ]);
      if (info) setBusinessInfo(info);
      if (links) setNavLinks(links);
    };
    fetchData();
  }, []);
  
  const handleCall = () => {
    const phone = businessInfo?.phone || '';
    window.location.href = `tel:${phone.replace(/[^0-9+]/g, '')}`;
  };

  const handleEmail = () => {
    const email = businessInfo?.email || '';
    window.location.href = `mailto:${email}`;
  };

  const handleDirections = () => {
    const address = encodeURIComponent(businessInfo?.address || '');
    window.open(`https://www.google.com/maps/search/${address}`, '_blank');
  };

  // Show loading state while businessInfo is being fetched
  if (!businessInfo) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* Final CTA Banner */}
      <section
        className="text-white py-16"
        style={{ background: 'linear-gradient(to right, #dc2626, #b91c1c)' }}
      >
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg md:text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Call Hanshi Jim Rivera directly to schedule a class. He'll answer your
            questions and get you on the schedule.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={`tel:${(businessInfo?.phone || '').replace(/[^0-9+]/g, '')}`}
              aria-label={`Call Owaza Martial Arts at ${businessInfo?.phone || ''}`}
              className="inline-flex items-center bg-white text-red-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <PhoneCall className="h-5 w-5 mr-2" />
              {businessInfo?.phone || 'Call to schedule'}
            </a>
          </div>
          <p className="mt-6 text-sm text-red-100/90">
            Appointment-only studio &middot; Spring Hill, FL &middot; Ask for Hanshi Jim Rivera
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Business Info */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src={`${CDN_URL}/images/owaza-logo.jpg`} 
                  alt="Owaza Martial Arts" 
                  className="h-12 w-12 rounded-full"
                />
                <div>
                  <h3 className="text-xl font-bold">{businessInfo?.name || 'Owaza Martial Arts'}</h3>
                  <p className="text-gray-400">Spring Hill, FL</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Expert martial arts training with a focus on safety, discipline,
                and real-world application. Serving Spring Hill and surrounding communities.
              </p>

              {/* Accreditation Badge */}
              <div className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 mb-4 w-fit max-w-full">
                <img
                  src="/step-up-for-students-logo.jpg"
                  alt="Step Up For Students"
                  className="h-10 w-auto flex-shrink-0"
                />
                <p className="text-xs text-gray-700 leading-tight">
                  Authorized Provider for Martial Arts, Wellness &amp; Fitness Classes
                  and a Martial Arts Supply Vendor
                </p>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/OwazaMartialArts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Facebook className="h-6 w-6" />
                </a>
                <a
                  href="https://www.tiktok.com/@owazamartialarts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <TikTokIcon className="h-6 w-6" />
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Contact Information</h3>
              <div className="space-y-4">
                <button 
                  onClick={handleDirections}
                  className="flex items-start space-x-3 text-gray-300 hover:text-white transition-colors text-left"
                >
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span>{businessInfo?.address || ''}</span>
                </button>
                
                <button 
                  onClick={handleCall}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  <span>{businessInfo?.phone || ''}</span>
                </button>
                
                <button 
                  onClick={handleEmail}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
                >
                  <Mail className="h-5 w-5" />
                  <span>{businessInfo?.email || ''}</span>
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Important Links</h3>
              <div className="space-y-3">
                {navLinks
                  .filter(link => link.show_in_footer && !link.parent_id)
                  .sort((a, b) => a.order - b.order)
                  .map(link => (
                    <a
                      key={link.id}
                      href={link.url}
                      className="block text-gray-300 hover:text-white transition-colors"
                    >
                      {link.title}
                    </a>
                  ))
                }
                <a
                  href={PRICE_LIST_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-300 hover:text-white transition-colors"
                >
                  Tuition &amp; Fees
                </a>

                <div className="pt-4 mt-2 border-t border-gray-800">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Programs</p>
                  <a href="/programs/kids-jujitsu" className="block text-gray-300 hover:text-white transition-colors">
                    Kids Jujitsu
                  </a>
                  <a href="/programs/aikido" className="block text-gray-300 hover:text-white transition-colors">
                    Aikido
                  </a>
                  <a href="/programs/karate-for-kids" className="block text-gray-300 hover:text-white transition-colors">
                    Karate for Kids
                  </a>
                  <a href="/programs/judo" className="block text-gray-300 hover:text-white transition-colors">
                    Judo
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © {currentYear} {businessInfo.name}. All rights reserved.
              </p>
              <p className="text-gray-400 text-sm">
                Appointment-only studio • Background checks required for adult memberships
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </>
  );
};

export default Footer;